import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth';
import { FacturasServicioService } from '../services/facturas-servicio.service';
import { ObraActivaService } from '../services/obra-activa.service';
import { PermisosService } from '../services/permisos.service';
import { AvisoService } from './aviso.service';
import {
  Tarjeta, accesosGeneralesVisibles, departamentosVisibles, destinoDe, resolverObra, usarBotonesVisibles,
} from './registro';

/** Cada cuánto, mientras la pestaña está abierta, se vuelven a pedir los permisos al volver a mirarla: cinco minutos. */
const RELEER_PERMISOS_DESPUES_DE_MS = 5 * 60_000;
/** Si la sesión se leyó hace menos que esto (el arranque de la app), abrir el inicio no la vuelve a pedir. */
const SESION_RECIEN_LEIDA_MS = 3000;

/**
 * Lo que el inicio y las pantallas de menú necesitan del registro, ya mirado con los permisos de quien está conectado: qué departamentos,
 * botones y tarjetas ve, y cómo se abre una tarjeta (resolviendo la obra con la que se trabaja).
 */
@Injectable({ providedIn: 'root' })
export class NavegacionService {
  private router = inject(Router);
  private permisos = inject(PermisosService);
  private auth = inject(AuthService);
  private obraActiva = inject(ObraActivaService);
  private facturas = inject(FacturasServicioService);
  private avisos = inject(AvisoService);

  /** Tarjetas que se ven por una vía distinta de una clave (hoy: el rol de aprobador de Facturas). */
  private readonly _concedidos = signal<ReadonlySet<string>>(new Set());
  readonly concedidos = this._concedidos.asReadonly();
  private concedidosLeidos = false;
  private ultimaLectura = 0;

  /** Los departamentos con sus botones y tarjetas, solo lo que el usuario puede ver. Se recalcula solo al cambiar sus permisos. */
  readonly departamentos = computed(() => departamentosVisibles(this.permisos, this.concedidos()));  readonly generales = computed(() => accesosGeneralesVisibles(this.permisos, this.concedidos()));
  constructor() {
    // Las migas y el «Atrás» del registro cuentan solo los botones que ESTE usuario ve (un departamento con uno solo no tiene pantalla propia).
    usarBotonesVisibles(id => this.departamentos().find(d => d.id === id)?.botones.length ?? 0);
    // El registro es global del proceso: al destruirse el servicio (cierre de la app o de la prueba) suelta lo que registró, para que nada de este perfil sobreviva a su dueño.
    inject(DestroyRef).onDestroy(() => usarBotonesVisibles(null));
  }

  /** No tiene ningún acceso a departamentos (los accesos generales no cuentan: los ve cualquiera). */
  readonly sinAccesos = computed(() => this.departamentos().length === 0);

  /**
   * Los roles de aprobación de Facturas dan acceso a ese módulo aunque no haya una clave de perfil. Se lee una sola vez; si falla, no hay
   * roles (y la tarjeta se decide solo por las claves). Es una llamada auxiliar: su error nunca rompe la pantalla.
   */
  async cargarConcedidos(forzar = false): Promise<void> {
    if (this.concedidosLeidos && !forzar) return;
    this.concedidosLeidos = true;
    try {
      const roles = await this.facturas.misRolesTodas();
      this._concedidos.set(new Set(roles.length > 0 ? ['facturas-aprobador'] : []));
    } catch {
      this._concedidos.set(new Set());
    }
  }

  /**
   * Deja los permisos al día: vuelve a pedir la sesión y los roles de aprobación. No bloquea el pintado (se muestra lo que hay y se
   * actualiza cuando llega) y si falla conserva lo anterior. Con `siHaceFalta` solo lo hace si pasó un rato desde la última vez.
   */
  async refrescarPermisos(siHaceFalta = false): Promise<void> {
    if (siHaceFalta && Date.now() - this.ultimaLectura < RELEER_PERMISOS_DESPUES_DE_MS) return;
    this.ultimaLectura = Date.now();
    // Al abrir la app, el arranque ya leyó la sesión hace un instante: no se pide otra vez (era un GET /auth/me repetido en cada carga).
    if (!(this.auth.sesionLeidaHace() < SESION_RECIEN_LEIDA_MS)) await this.auth.refrescarSesion();
    await this.cargarConcedidos(true);
  }

  /**
   * Abre una tarjeta. Una pantalla de UNA obra se abre con la obra activa si el usuario puede usar el módulo en ella; si no, con la primera
   * donde sí puede, y se le dice (no hay pantalla intermedia de «elige obra»). Sin ninguna obra donde usarla se avisa en vez de navegar.
   * Las que se abren en una pestaña nueva (Soporte) no cambian la pantalla actual.
   */
  async abrir(t: Tarjeta): Promise<void> {
    if (t.nuevaPestana) {
      window.open(destinoDe(t, null) ?? t.ruta, '_blank', 'noopener');
      return;
    }
    if (t.obras === 'una' && !this.obraActiva.cargado()) await this.obraActiva.cargar();
    const r = resolverObra(t, this.obraActiva.obras(), this.obraActiva.obraActivaId(), this.permisos);
    if (r.accion === 'sin-obras') {
      this.avisos.mostrar(`No tienes ninguna obra donde abrir «${t.label}». Pide que te asignen una obra con acceso a ese módulo.`, 'alerta', 7000);
      return;
    }
    if (r.accion === 'abrir') {
      const destino = destinoDe(t, null);
      if (destino !== null) await this.router.navigateByUrl(destino);
      return;
    }
    if (r.obra.id !== this.obraActiva.obraActivaId()) {
      try {
        await this.obraActiva.elegir(r.obra.id);
      } catch {
        this.avisos.mostrar('No se pudo cambiar a esa obra. Inténtalo de nuevo en un momento.', 'alerta', 7000);
        return;
      }
      if (r.cambia) {
        const nombre = r.obra.codigo ? `${r.obra.codigo} — ${r.obra.nombre}` : r.obra.nombre;
        this.avisos.mostrar(`Cambiamos a ${nombre} porque tienes acceso a ella en este módulo.`);
      }
    }
    const destino = destinoDe(t, r.obra.id);
    if (destino !== null) await this.router.navigateByUrl(destino);
  }
}
